import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowRight, ArrowLeft, User } from "lucide-react";

// ─── FULL ARTICLE CONTENT (keyed by slug) ────────────────────────────────────
const blogContent = {
  "10-proven-strategies-eliminate-empty-beds-without-brokerage": `
Every PG owner knows the "Broker Tax." You work hard to maintain your property, only to give away 15 to 30 days of rent to a middleman just for finding a tenant. In a competitive market like Delhi, these costs eat into your expansion capital.

If you want to reach "Million Dollar" status with your property business, you need to stop outsourcing your sales. Here are 10 proven strategies to fill your beds directly.

## 1. Claim Your "Digital Front Door" (Google Local SEO)

When a student moves to Karol Bagh, the first thing they search is "PG near Vision IAS" or "Best PG in Karol Bagh."

**The Strategy:** Create a Google Business Profile. Upload high-quality, wide-angle photos of your rooms.

**The Pro Tip:** Regularly post "updates" on your profile like: "2 Beds left in our premium girls' wing near Vajiram & Ravi." Google loves fresh content and will rank you higher than inactive competitors.

## 2. The "WhatsApp Status" Referral Loop

Your current happy tenants are your best sales team.

**The Strategy:** Offer a "Referral Bonus" (e.g., ₹2,000 off next month's rent) for every successful onboarding.

**The Pro Tip:** Give your tenants a beautiful "Verified by AapkaPG" digital card to put on their WhatsApp Status. Their friends—often fellow aspirants—will see it and reach out directly.

## 3. Hyper-Local Institute Proximity Marketing

In student hubs, distance is the #1 deciding factor.

**The Strategy:** Instead of saying you are in "Karol Bagh," say you are "90 seconds away from the Vision IAS library."

**The Pro Tip:** Place QR code stickers at popular student "Chai Points" or libraries. A simple hook like "Tired of Broker Fees? Scan to find direct beds at [Your PG Name]" works wonders.

## 4. Leverage the "Trust Audit" Concept

Students and parents are increasingly wary of "fake" listings.

**The Strategy:** Get a 50-point safety and hygiene audit. Display your "Trust Certificate" prominently at your reception and on your digital listings.

**Why it works:** When a parent sees a professional audit (like the AapkaPG Trust Shield), they stop looking at other cheaper, unverified options.

## 5. Move from WhatsApp to "Room Links"

Stop sending 20 random photos on WhatsApp to every inquiry. It looks unprofessional and clutters the student's phone.

**The Strategy:** Use a single, clean link that shows the room photos, current occupancy, and transparent pricing.

**The Pro Tip:** High-revenue owners use AapkaPG.com to generate these links. It makes you look like a "Smart Property," not a local landlord.

## 6. Optimize for "Parental Peace of Mind"

Remember, for many students, the parent is the one paying the security deposit.

**The Strategy:** Highlight features that parents care about: 24/7 CCTV, Biometric entry, and Digital KYC.

**The Pro Tip:** Mention that you use professional software for visitor management. A parent is 50% more likely to book a room if they know their child's safety is managed digitally.

## 7. The "Platform Parasite" Strategy (Free Classifieds)

Don't just list on one site. Flood the free ones.

**The Strategy:** Use OLX, Facebook Marketplace, and Quikr.

**The Pro Tip:** Refresh your listings every 48 hours. Most owners "list and forget." By refreshing, you stay at the top of the search results where the most desperate searchers are looking.

## 8. "Real" Video Tours (Reels & Shorts)

Static photos are dead. Students want to see the "vibe."

**The Strategy:** Record a simple 30-second walkthrough on your phone. No fancy editing—just show the room, the bathroom, and the view from the balcony.

**The Pro Tip:** Post these as Instagram Reels with local hashtags like #UpscAspirants or #OldRajinderNagar.

## 9. Transparency as a Sales Tool

Hidden costs (electricity, laundry, "maintenance") are the #1 reason students leave after a month.

**The Strategy:** Be 100% upfront about costs.

**The Pro Tip:** Use a professional management tool to provide digital rent receipts. When a student knows exactly what they are paying for, they trust you more and are less likely to leave for a "cheaper" (but dishonest) competitor.

## 10. Focus on "Churn Reduction"

The cheapest bed to fill is the one that's already occupied.

**The Strategy:** Use an app-based complaint system. If a student can report a leaking tap on an app and see it fixed in 2 hours, they will never leave.

**The Pro Tip:** High retention rates mean you don't need a marketing budget. Your PG stays full through word-of-mouth alone.

## Conclusion: Systemise to Scale

Eliminating brokerage isn't about working harder; it's about having the right system. When you use a platform like AapkaPG.com, you get the listing tools to find tenants and the management tools to keep them.

Ready to turn your PG into a high-occupancy, broker-free business? **Join the AapkaPG Partner Network Today.**
`,

  "how-to-choose-the-right-pg": `
Moving to a new city is exciting—but finding the right PG can feel overwhelming. Between fake listings, hidden charges, and pushy brokers, first-time renters often end up in a place they regret within the first month.

This guide walks you through everything you need to evaluate before paying that security deposit.

## 1. Define Your Non-Negotiables First

Before you even open a search app, write down what you absolutely cannot compromise on.

**The Strategy:** List 3 non-negotiables. For most renters, these are: distance to office/college, food quality, and Wi-Fi.

**The Pro Tip:** Rank them. If "distance" is #1, don't let a fancy gym at a far-away PG distract you. Cheap PGs that add 90 minutes of commute daily are the most expensive PGs in disguise.

## 2. Set a Realistic All-Inclusive Budget

The advertised rent is rarely what you'll actually pay.

**The Strategy:** Always ask for an "all-inclusive" monthly figure that covers rent, electricity, water, Wi-Fi, food, and laundry.

**The Pro Tip:** Add a 15% buffer for surprise charges (generator backup, festival cleaning fees, "maintenance"). If the owner refuses to commit to a fixed all-in number, that's a red flag.

## 3. Verify the Location—In Person and On a Weekday

Photos lie. Maps lie. Your own feet don't.

**The Strategy:** Visit the PG between 7–10 PM on a weekday to check the real neighborhood vibe—lighting, safety, noise, and crowd.

**The Pro Tip:** Walk from the PG to the nearest metro/bus stop. If you wouldn't walk it alone at 10 PM, the location isn't right—no matter how nice the room looks.

## 4. Inspect the Room Like a Detective

Don't just glance at the bed. Inspect every corner.

**The Strategy:** Check water pressure in the bathroom, test the fan and AC, open every cupboard, and look under the bed for dust or pests.

**The Pro Tip:** Take photos of any existing damage on move-in day. This protects your security deposit when you leave.

## 5. Taste the Food Before You Commit

Food is the #1 reason tenants change PGs within 60 days.

**The Strategy:** Ask if you can have a meal at the PG before signing. Most quality PGs will say yes.

**The Pro Tip:** Ask current tenants—not the owner—how the food is. Find one in the corridor and ask: "Honestly, would you eat here next month if rent was the same elsewhere?"

## 6. Read the Agreement—Every Single Line

A handshake is not a contract.

**The Strategy:** Insist on a written agreement that clearly mentions notice period, security deposit refund timeline, lock-in period, and exit conditions.

**The Pro Tip:** Watch for "non-refundable" clauses on the deposit, vague "damages" clauses, and 3-month lock-ins. A fair PG agreement has a 30-day notice and a refundable deposit returned within 15 days.

## 7. Verify Safety Infrastructure

Safety is not a feature—it's a requirement.

**The Strategy:** Confirm 24/7 CCTV at entry/exit, biometric or app-based entry, and a visitor logbook.

**The Pro Tip:** Ask about the warden's response time at night. A PG with no on-site staff between 10 PM and 7 AM is not safe, regardless of the amenities.

## 8. Talk to At Least Two Current Tenants

Owners sell. Tenants tell the truth.

**The Strategy:** Ask the owner to introduce you to two current residents. Then ask them about water supply, food consistency, and how complaints are handled.

**The Pro Tip:** If the owner hesitates or only introduces you to "his favorite" tenant, walk away.

## 9. Choose Verified Listings Over Random Brokers

The cheapest bed isn't on a flyer—it's on a verified platform.

**The Strategy:** Use platforms like AapkaPG that display the Trust Shield, real photos, and current occupancy data.

**The Pro Tip:** Verified PGs save you brokerage (often ₹15,000–₹30,000) and protect you from "ghost listings"—rooms that don't exist or look nothing like the photos.

## 10. Trust Your Gut

If something feels off—the owner is too pushy, the price seems too good, the photos look stock—it probably is.

**The Strategy:** Sleep on it for 24 hours before paying any deposit.

**The Pro Tip:** A genuinely good PG will still be available tomorrow. A scammer will pressure you with "3 other people are interested right now."

## Conclusion: The Right PG Pays for Itself

The right PG saves you commute time, money, energy, and emotional bandwidth. Take the extra two days to verify—it will save you two months of regret.

**Start your verified PG search at AapkaPG.com today.**
`,

  "trust-shield-what-it-means": `
When you book a PG online, the biggest question in your mind is simple: "Is this real?"

The AapkaPG Trust Shield exists to answer that question. It's a 50-point audit that every Trust Shield-certified PG must pass before being listed on our platform. Here's exactly what we check—and why it matters to you.

## What Is the Trust Shield?

The Trust Shield is not a paid badge. It's an on-ground verification done by AapkaPG-trained inspectors who physically visit, photograph, and audit the property.

A PG cannot buy this badge. They can only earn it.

## The 5 Pillars of the Audit

Our 50-point checklist is grouped into five categories. A PG must score at least 90% to qualify.

## 1. Safety & Security (12 Points)

Your physical safety is non-negotiable.

**What we check:** Working CCTV at entry/exit, biometric or app-based access, fire extinguishers, emergency exits, on-site warden availability, visitor logbook, and women's safety provisions.

**Why it matters:** A PG without functioning CCTV or after-hours staff is one incident away from disaster.

## 2. Hygiene & Cleanliness (10 Points)

A clean PG isn't a luxury—it's the baseline.

**What we check:** Daily room cleaning, bathroom hygiene, kitchen sanitation, pest control records, and water quality (TDS tested).

**Why it matters:** 70% of PG complaints we receive are about hygiene. Trust Shield ensures you don't become part of that statistic.

## 3. Food Quality & Kitchen Standards (8 Points)

Bad food drives more tenants out than bad rooms.

**What we check:** Kitchen license, separate veg/non-veg utensils, FSSAI compliance, freshness of ingredients, and weekly menu variation.

**Why it matters:** Many PGs cut corners on food to boost margins. Our audit ensures you're not paying for a chapati made yesterday.

## 4. Infrastructure & Amenities (12 Points)

Promised amenities must actually work.

**What we check:** Wi-Fi speed (real-time tested), power backup duration, water supply consistency, AC/cooling, hot water availability, and laundry facilities.

**Why it matters:** "Wi-Fi available" means nothing if it drops every evening. We test it during peak hours.

## 5. Transparency & Tenant Rights (8 Points)

You should never be surprised by hidden fees.

**What we check:** Written agreements, clear pricing breakdown, notice period clarity, deposit refund policy, and complaint resolution system.

**Why it matters:** This pillar protects your money. A Trust Shield PG cannot legally hit you with surprise "festival fees" or arbitrary deductions.

## How the Audit Process Works

1. **Owner Application** — The PG owner requests a Trust Shield audit.
2. **Surprise Visit** — Our inspector arrives unannounced. No staged tours.
3. **50-Point Verification** — Each item is photographed and scored.
4. **Tenant Interviews** — We talk privately to at least 3 current tenants.
5. **Report Card** — A score is generated. 90%+ earns the Shield.
6. **Quarterly Re-Audits** — The Shield is not permanent. We re-verify every 90 days.

## What Trust Shield Means for Renters

When you book a Trust Shield PG, you get verified photos, guaranteed amenities, refund protection if the PG materially differs from its listing, and direct AapkaPG support for any disputes.

## What Trust Shield Means for Owners

For PG owners, the Shield is a competitive moat: 3x more inquiries than non-verified listings, higher average rent, lower tenant churn, and free marketing through the AapkaPG verified directory.

## Why We Don't Compromise

We've turned down hundreds of PGs that didn't meet the standard. Some offered to pay more. We said no.

The Trust Shield only works if it actually means something.

## Conclusion: Look for the Shield

Before you pay any deposit, look for the AapkaPG Trust Shield on the listing. It's the difference between hoping for a good PG and knowing you have one.

**Browse Trust Shield-verified PGs at AapkaPG.com.**
`,

  "digital-pg-management-tools": `
The PG industry in India is going through its biggest shift in 20 years. Owners who run their business on WhatsApp groups and paper diaries are losing tenants—and revenue—to owners who've gone digital.

Here are the 5 categories of tools that separate ₹50 lakh/year PG businesses from ₹5 crore/year ones.

## 1. Occupancy & Bed Management Dashboards

If you're tracking beds in a notebook, you're losing money. Period.

**The Strategy:** Use a real-time occupancy dashboard that shows every room, every bed, current tenant, lease end date, and rent due status.

**Why it matters:** Most owners discover an empty bed 3–7 days after it goes vacant. A dashboard alerts you the moment a notice is filed, so you can start re-listing immediately.

**The Pro Tip:** Connect your dashboard to your listing platform. The moment a bed goes vacant, it should auto-publish to AapkaPG.com.

## 2. Digital Rent Collection & Receipts

Cash creates chaos. Digital creates compounding.

**The Strategy:** Move 100% of collections to UPI or auto-debit, with auto-generated digital receipts.

**Why it matters:** Digital collection reduces "I'll pay tomorrow" excuses by 80%. Plus, it gives you a clean financial record for taxes and loans.

**The Pro Tip:** Set up auto-reminders 3 days before due date, on due date, and 3 days after. Most late payments aren't intentional—they're forgotten.

## 3. Complaint & Maintenance Ticketing

Tenants don't leave because of one big issue. They leave because of 10 small ones that were ignored.

**The Strategy:** Use an in-app ticketing system where tenants can raise complaints (leaky tap, broken AC) and track resolution.

**Why it matters:** A 24-hour resolution SLA, transparently tracked, builds massive trust. Tenants who feel heard, stay.

**The Pro Tip:** Publish your average resolution time on your listing. "Average complaint resolved in 4.2 hours" is a stronger sales line than "great service."

## 4. KYC & Digital Onboarding

Paper agreements are a legal time bomb.

**The Strategy:** Use a digital KYC tool that captures Aadhaar, PAN, parent contact, and emergency contact, then generates a legally valid e-stamped agreement.

**Why it matters:** When (not if) a dispute happens, you'll have a court-admissible record. It also protects you from fraud—students using fake IDs to skip rent.

**The Pro Tip:** Digital onboarding cuts move-in time from 2 hours to 15 minutes. Faster onboarding = better first impression = lower 30-day churn.

## 5. Visitor & Attendance Management

Parents care about safety more than amenities.

**The Strategy:** Use a digital visitor log (QR code-based) and optional in/out tracking for tenants.

**Why it matters:** When a parent calls asking "Has my daughter come back tonight?", you should be able to answer in 5 seconds—not 5 minutes.

**The Pro Tip:** Send weekly safety reports to parents (number of visitors logged, response time to night-time entries). Parents will renew without negotiation.

## Bonus: An All-In-One Platform Beats 5 Separate Tools

Here's the trap most owners fall into: they buy 5 different SaaS tools that don't talk to each other.

The result? You're spending 3 hours a day copy-pasting data between dashboards.

**The Better Approach:** Use an integrated platform like AapkaPG that combines listings, occupancy, payments, complaints, and onboarding into one login.

## What This Looks Like in Numbers

Owners who go fully digital typically see 30% lower churn, 15% higher revenue, 50% less admin time, and 2x more inquiries.

That's the difference between a side-hustle PG and a real business.

## Conclusion: The Digital Divide Is Here

In 2026, your tenants are digital natives. Their parents are digital-cautious. Both expect the businesses they trust to behave like the businesses they use—Swiggy, Uber, Amazon.

Your PG can be that business. Or it can be the one they switch from.

**Upgrade your PG operations with AapkaPG today.**
`,

  "zero-brokerage-movement": `
For 30 years, brokerage has been the unwritten tax of Indian renting. You find a place. You like it. The broker takes 30 days of your rent for "introducing" you to a building you could've walked into yourself.

That model is dying. And it's not coming back.

## Why Brokerage Existed in the First Place

To be fair, brokerage made sense in 1995.

There was no internet. No photos. No reviews. The broker was a human search engine—he knew which buildings had vacancies, which owners were reasonable, and which neighborhoods were safe.

For that information arbitrage, he charged 15–30 days of rent. Fair trade.

## What Changed

Three forces broke the broker model:

**1. Smartphones in every hand.** Every tenant now has more search power than any broker ever had.

**2. Verified listing platforms.** Photos, reviews, real-time occupancy, and trust badges replaced "trust me bhaiya."

**3. Digital onboarding.** What used to require 5 broker visits now takes one video call.

The broker's value proposition—information access—has been completely commoditized.

## The Math No One Talks About

Let's run the numbers on a typical PG room in Bangalore: Rent ₹15,000/month, Brokerage 1 month's rent = ₹15,000. Multiply that across 1.2 crore PG beds in India.

That's ₹18,000 crore extracted by middlemen every year. For information that's freely available online.

## Who's Pushing the Movement

Three groups are accelerating zero-brokerage:

**Tenants** — Especially Gen Z renters who grew up on direct-to-consumer apps. To them, paying a broker fee feels as outdated as paying for SMS.

**Owners** — Smart owners realized brokers don't add value; they extract it. A vacant bed sold by a broker is a vacant bed the owner could've sold themselves with the right tools.

**Platforms** — Companies like AapkaPG built the tools that replaced the broker. We provide the listing, the verification, the onboarding, and the trust—at zero brokerage.

## The Resistance (and Why It Will Fail)

Brokers aren't going quietly. But every counter-argument falls apart: verified platforms do better on-ground checks than any single broker, platforms stay with you after the deal closes unlike brokers who disappear, and "off-market deals" is just code for lower-quality listings afraid of reviews.

## What Zero Brokerage Actually Means

Zero brokerage doesn't mean zero cost—platforms have costs too. But it means owners pay platforms a small subscription instead of huge one-time broker fees, tenants pay nothing beyond rent and a refundable deposit, verification is built-in not optional, and listings stay live until the bed is filled.

## Why It's Inevitable

Every consumer industry has gone through this transition. Travel agents → Booking.com. Stockbrokers → Zerodha. Newspaper classifieds → OLX.

The pattern is identical: when information becomes free, the middleman who sold information becomes obsolete.

PG brokers are next. The only question is how fast.

## Conclusion: The Future Is Direct

In 5 years, paying brokerage to rent a PG will sound as absurd as paying a travel agent to book a flight.

You don't have to wait for the future. It's already here.

**Find your next PG, broker-free, at AapkaPG.com.**
`,

  "pg-safety-checklist-parents": `
Sending your child to a new city for studies or their first job is one of the hardest decisions a parent makes. You can't be there every day—but you can make sure the place they live is safe before they move in.

This 15-point checklist is what every parent should verify before paying any security deposit.

## Category 1: Physical Security

## 1. CCTV Coverage at All Entry/Exit Points

**What to check:** Confirm working CCTV at the main entrance, common areas, and floor corridors.

**Red flag:** "We're installing it next month."

## 2. Biometric or App-Based Entry

**What to check:** A physical key or padlock is not enough. Modern PGs use fingerprint, face recognition, or app-based access.

**Red flag:** A single shared key for the whole floor.

## 3. On-Site Warden 24/7

**What to check:** Ask who is physically present at the PG between 10 PM and 7 AM.

**Red flag:** "The owner stays nearby and can come if needed."

## 4. Fire Safety Equipment

**What to check:** Visible, accessible fire extinguishers on every floor, with a valid service date.

**Red flag:** Extinguishers placed inside a locked room.

## Category 2: Tenant Verification

## 5. KYC for Every Resident

**What to check:** Confirm the PG verifies Aadhaar/PAN of every resident, not just your child.

**Red flag:** "We don't ask too many questions—it's a friendly place."

## 6. Visitor Logbook

**What to check:** Every visitor (including delivery agents) should be logged with name, ID, and time.

**Red flag:** No logbook, or one that hasn't been updated in days.

## 7. Curfew or Late-Entry Policy

**What to check:** Understand the late-entry policy. A reasonable PG allows late entry but with notification.

**Red flag:** No policy at all (means anyone can come and go anytime).

## Category 3: Health & Hygiene

## 8. Water Quality

**What to check:** Ask about the source and last RO maintenance date. A TDS tester gives an instant reading.

**Red flag:** "The water is fine, everyone drinks it."

## 9. Kitchen Hygiene

**What to check:** Visit the kitchen unannounced. Look at the storage, the cleaning practices, and the refrigerator.

**Red flag:** Food stored in open containers or visible pest droppings.

## 10. Bathroom Cleanliness

**What to check:** Bathrooms should be cleaned at least twice daily in shared PGs.

**Red flag:** Mold on walls, broken tiles, or no exhaust fan.

## Category 4: Emergency Preparedness

## 11. Nearest Hospital Distance

**What to check:** Confirm the closest hospital is within 5 km, and the PG has a tie-up or known contact for emergencies.

**Red flag:** No one at the PG can name the nearest hospital.

## 12. Emergency Contact Protocol

**What to check:** Your number should be on file. The PG should commit to calling you within 30 minutes of any emergency.

**Red flag:** "We usually contact the parent the next day."

## 13. First-Aid Kit Availability

**What to check:** A stocked first-aid kit at the reception, with bandages, antiseptic, paracetamol, and ORS.

**Red flag:** "We send someone to the medical store if needed."

## Category 5: Tenant Wellbeing

## 14. Existing Tenant Reviews

**What to check:** Talk privately to 2–3 current tenants. Ask about safety incidents, food, and complaint response.

**Red flag:** The owner refuses to let you speak to existing tenants.

## 15. Trust Shield Verification

**What to check:** Look for the AapkaPG Trust Shield badge on the listing—it confirms a 50-point audit by independent inspectors.

**Red flag:** Random claims of being "verified" without any third-party proof.

## How to Use This Checklist

Print this list. Carry it on your visit. Don't be embarrassed to check items off in front of the owner—a good PG owner will appreciate your diligence.

If a PG fails on more than 3 items, walk away. There are 12,500+ verified PGs on AapkaPG. Your child deserves one that ticks all the boxes.

## Conclusion: Verify, Then Trust

Parental peace of mind isn't built on hope. It's built on verification.

Use this checklist. Look for the Trust Shield. And know that the right PG isn't just a roof—it's a safe second home.

**Find Trust Shield-verified PGs for your child at AapkaPG.com.**
`,
};

// ─── BLOG POSTS METADATA ──────────────────────────────────────────────────────
const blogPosts = [
  {
    slug: "10-proven-strategies-eliminate-empty-beds-without-brokerage",
    title: "10 Proven Strategies to Eliminate Empty Beds Without Paying Brokerage",
    excerpt: "Every PG owner knows the \"Broker Tax.\" You work hard to maintain your property, only to give away 15 to 30 days of rent to a middleman. Here are 10 proven strategies to fill your beds directly.",
    author: "AapkaPG Team", date: "May 5, 2026", readTime: "8 min read", category: "PG Owners", featured: true,
  },
  {
    slug: "how-to-choose-the-right-pg",
    title: "How to Choose the Right PG: A Complete Guide for First-Time Renters",
    excerpt: "Moving to a new city for work or studies? Here's everything you need to know about finding a PG that fits your budget, lifestyle, and safety needs.",
    author: "AapkaPG Team", date: "April 28, 2026", readTime: "6 min read", category: "Renters",
  },
  {
    slug: "trust-shield-what-it-means",
    title: "What the AapkaPG Trust Shield Means for You",
    excerpt: "Our 50-point safety and hygiene audit is the gold standard for PG verification in India. Learn what we check and why it matters.",
    author: "Arjun Kapoor", date: "April 15, 2026", readTime: "5 min read", category: "Trust & Safety",
  },
  {
    slug: "digital-pg-management-tools",
    title: "5 Digital Tools Every PG Owner Needs in 2026",
    excerpt: "From occupancy dashboards to digital rent receipts, discover the tech stack that separates high-revenue PG businesses from the rest.",
    author: "AapkaPG Team", date: "April 2, 2026", readTime: "7 min read", category: "PG Owners",
  },
  {
    slug: "zero-brokerage-movement",
    title: "The Zero Brokerage Movement: Why It's Inevitable",
    excerpt: "Brokerage has been the norm in Indian rentals for decades. But technology is changing the game. Here's why zero-brokerage is the future.",
    author: "AapkaPG Team", date: "March 20, 2026", readTime: "6 min read", category: "Industry",
  },
  {
    slug: "pg-safety-checklist-parents",
    title: "PG Safety Checklist: What Every Parent Should Verify",
    excerpt: "Sending your child to a new city? Use this 15-point checklist to evaluate any PG before paying the security deposit.",
    author: "AapkaPG Team", date: "March 8, 2026", readTime: "5 min read", category: "Renters",
  },
];

// ─── MARKDOWN RENDERER ────────────────────────────────────────────────────────
function MarkdownRenderer({ content }) {
  const lines = content.trim().split("\n");
  const elements = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.startsWith("## ")) {
      elements.push(
        <h2 key={i} className="bl-article-h2">
          {line.replace("## ", "")}
        </h2>
      );
    } else if (line.trim() === "") {
      // skip blank lines
    } else {
      const html = line.replace(/\*\*(.+?)\*\*/g, '<strong class="bl-fw-600">$1</strong>');
      elements.push(
        <p key={i} className="bl-muted bl-article-p" dangerouslySetInnerHTML={{ __html: html }} />
      );
    }
  }
  return <>{elements}</>;
}

// ─── BLOG DETAIL ──────────────────────────────────────────────────────────────
function BlogDetail({ post, onBack }) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [post.slug]);

  const content = blogContent[post.slug];
  return (
    <div>
      <section className="bl-hero bl-hero--detail">
        <div className="bl-hero__bg" />
        <div className="bl-container bl-container--narrow bl-hero__content">
          <button onClick={onBack} className="bl-back-btn">
            <ArrowLeft className="bl-back-icon" /> Back to Blog
          </button>
          <span className="bl-badge bl-detail-badge">{post.category}</span>
          <h1 className="bl-fluid-4xl bl-fw-800 bl-detail-title">{post.title}</h1>
          <div className="bl-post-meta">
            <span><User className="bl-meta-icon" /> {post.author}</span>
            <span><Calendar className="bl-meta-icon" /> {post.date}</span>
            <span><Clock className="bl-meta-icon" /> {post.readTime}</span>
          </div>
        </div>
      </section>

      <section className="bl-section">
        <div className="bl-container bl-container--narrow">
          <article className="bl-article-body">
            <MarkdownRenderer content={content} />
          </article>
        </div>
      </section>

      <section className="bl-section">
        <div className="bl-container">
          <div className="bl-cta-box">
            <h2 className="bl-fluid-2xl bl-fw-800 bl-cta-title">Ready to go broker-free?</h2>
            <p className="bl-cta-sub">Join the AapkaPG Partner Network and start filling beds directly.</p>
            <Link to="/listings" className="bl-btn-primary bl-cta-btn">Get Started</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

// ─── BLOG LIST ────────────────────────────────────────────────────────────────
export default function Blog() {
  const [activeSlug, setActiveSlug] = useState(null);
  const activePost = activeSlug ? blogPosts.find(p => p.slug === activeSlug) : null;

  if (activePost) {
    return (
      <div className="bl-root">
        <BlogDetail post={activePost} onBack={() => setActiveSlug(null)} />
      </div>
    );
  }

  const featured = blogPosts.find(p => p.featured);
  const rest     = blogPosts.filter(p => !p.featured);

  return (
    <div className="bl-root">

      {/* Hero */}
      <section className="bl-hero">
        <div className="bl-hero__bg" />
        <div className="bl-hero__glow" />
        <div className="bl-container bl-hero__content">
          <span className="bl-badge"><span className="bl-badge__dot" /> Blog</span>
          <h1 className="bl-fluid-hero bl-hero__title">
            Insights for <span className="bl-primary">PG Owners</span> & Renters
          </h1>
          <p className="bl-fluid-lg bl-hero__sub">
            Tips, strategies, and stories from the frontlines of India's PG revolution.
          </p>
        </div>
      </section>

      {/* Featured */}
      {featured && (
        <section className="bl-section bl-section--featured">
          <div className="bl-container">
            <div className="bl-featured" onClick={() => setActiveSlug(featured.slug)}>
              <div className="bl-featured__left">
                <span className="bl-featured__badge">Featured</span>
                <h2 className="bl-fluid-2xl bl-fw-800 bl-featured__title">{featured.title}</h2>
                <p className="bl-featured__excerpt">{featured.excerpt}</p>
                <div className="bl-featured__meta">
                  <span><Calendar className="bl-meta-icon" /> {featured.date}</span>
                  <span><Clock className="bl-meta-icon" /> {featured.readTime}</span>
                </div>
              </div>
              <div className="bl-featured__right">
                <span className="bl-badge">{featured.category}</span>
                <p className="bl-muted bl-featured__right-excerpt">{featured.excerpt}</p>
                <span className="bl-read-more">
                  Read full article <ArrowRight className="bl-read-more-icon" />
                </span>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* All articles */}
      <section className="bl-section bl-section--muted">
        <div className="bl-container">
          <h2 className="bl-fluid-2xl bl-fw-800 bl-articles-heading">All Articles</h2>
          <div className="bl-posts-grid">
            {rest.map(post => (
              <div key={post.slug} className="bl-post-card" onClick={() => setActiveSlug(post.slug)}>
                <div className="bl-post-card__strip" />
                <div className="bl-post-card__body">
                  <span className="bl-badge bl-post-card__badge">{post.category}</span>
                  <h3 className="bl-fw-700 bl-post-card__title">{post.title}</h3>
                  <p className="bl-muted bl-post-card__excerpt">{post.excerpt}</p>
                  <div className="bl-post-card__footer">
                    <span><User className="bl-meta-icon" /> {post.author}</span>
                    <span><Clock className="bl-meta-icon" /> {post.readTime}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}